import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { AssetValuation } from '../components/AssetValuation';
import ValuationHistory from '../components/ValuationHistory';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { formatDate, getCategoryColor, getCategoryImage } from '../utils/helpers';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  MapPin, 
  Calendar,
  Shield,
  FileText,
  Wrench,
  Plus,
  Car,
  Palette,
  Wine,
  Gem,
  ShoppingBag,
  Watch,
  TrendingUp,
  TrendingDown,
  LineChart,
  Hexagon,
  KeyRound,
  Loader2,
  Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TokenizeCeremony } from '../components/TokenizeCeremony';
import { DigitalTwinViewer } from '../components/DigitalTwinViewer';
import { ProvenanceShareCard } from '../components/ProvenanceShareCard';
import { SellerReadiness } from '../components/SellerReadiness';
import { FEATURES } from '../config/features';

const iconMap = {
  car: Car,
  art: Palette,
  wine: Wine,
  jewelry: Gem,
  handbag: ShoppingBag,
  watch: Watch,
};

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { formatCurrency } = useCurrency();
  const [asset, setAsset] = useState(null);
  const [maintenance, setMaintenance] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [showSellerReadiness, setShowSellerReadiness] = useState(false);
  const [pendingTokenIdToList, setPendingTokenIdToList] = useState(null);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState({ title: '', description: '', scheduled_date: '', cost: '' });
  const [documentForm, setDocumentForm] = useState({ name: '', document_type: 'insurance', file_url: '', notes: '', file: null });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showTokenizeModal, setShowTokenizeModal] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.uploadAssetImage(id, formData);
      toast.success('Photo uploaded successfully');
      setAsset(prev => ({ ...prev, image_url: res.data.url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [assetRes, maintenanceRes, documentsRes] = await Promise.all([
        api.getAsset(id),
        api.getMaintenance(id),
        api.getDocuments(id)
      ]);
      setAsset(assetRes.data);
      setMaintenance(maintenanceRes.data);
      setDocuments(documentsRes.data);
      // Fetch token data if tokenized
      if (assetRes.data?.token_status && assetRes.data.token_status !== 'failed') {
        try {
          const tokens = await api.getMyTokens();
          const tk = tokens.data?.find(t => t.asset_id === assetRes.data.id);
          if (tk) setTokenData(tk);
        } catch {}
      }
    } catch (error) {
      console.error('Failed to fetch asset:', error);
      toast.error('Asset not found');
      navigate('/assets');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteAsset(id);
      toast.success('Asset deleted');
      navigate('/assets');
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const handleAddMaintenance = async (e) => {
    e.preventDefault();
    try {
      await api.createMaintenance({
        asset_id: id,
        title: maintenanceForm.title,
        description: maintenanceForm.description,
        scheduled_date: maintenanceForm.scheduled_date,
        cost: maintenanceForm.cost ? parseFloat(maintenanceForm.cost) : null
      });
      toast.success('Maintenance scheduled');
      setShowMaintenanceDialog(false);
      setMaintenanceForm({ title: '', description: '', scheduled_date: '', cost: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add maintenance');
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (uploadingDoc) return;
    setUploadingDoc(true);
    try {
      if (documentForm.file) {
        // Real file upload path
        const fd = new FormData();
        fd.append('file', documentForm.file);
        fd.append('asset_id', id);
        fd.append('name', documentForm.name);
        fd.append('document_type', documentForm.document_type);
        if (documentForm.notes) fd.append('notes', documentForm.notes);
        await api.uploadDocument(fd);
        toast.success('Document uploaded');
      } else if (documentForm.file_url) {
        // Legacy URL-only path
        await api.createDocument({
          asset_id: id,
          name: documentForm.name,
          document_type: documentForm.document_type,
          file_url: documentForm.file_url,
          notes: documentForm.notes,
        });
        toast.success('Document added');
      } else {
        toast.error('Please attach a file or provide a URL');
        return;
      }
      setShowDocumentDialog(false);
      setDocumentForm({ name: '', document_type: 'insurance', file_url: '', notes: '', file: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteMaintenance = async (maintenanceId) => {
    try {
      await api.deleteMaintenance(maintenanceId);
      toast.success('Maintenance record deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await api.deleteDocument(docId);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!asset) return null;

  const Icon = iconMap[asset.category] || Palette;
  const gain = asset.current_value - asset.purchase_price;
  const gainPercent = ((gain / asset.purchase_price) * 100).toFixed(1);
  const isPositive = gain >= 0;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="asset-detail-page">
        {/* Back Button */}
        <Link 
          to="/assets" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-6 transition-colors"
          data-testid="back-to-assets"
        >
          <ArrowLeft size={18} />
          Back to Assets
        </Link>

        {/* Hero Section */}
        <div className="relative h-64 md:h-80 lg:h-96 mb-8 overflow-hidden luxury-card p-0 group">
          <img 
            src={asset.image_url ? (asset.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${asset.image_url}` : asset.image_url) : getCategoryImage(asset.category)}
            alt={asset.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          {/* Upload Photo Button */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleImageUpload}
            data-testid="asset-image-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2 bg-black/60 backdrop-blur-sm border border-gold/30 rounded-lg text-xs text-gold hover:bg-black/80 hover:border-gold/60 transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 z-10"
            data-testid="upload-photo-btn"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            {uploading ? 'Uploading...' : 'Upload Photo'}
          </button>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <span className={`inline-flex items-center gap-2 px-3 py-1 text-xs uppercase tracking-wider bg-black/60 backdrop-blur-sm mb-4 ${getCategoryColor(asset.category)}`}>
              <Icon size={14} />
              {asset.category}
            </span>
            <h1 className="font-serif text-3xl md:text-4xl text-white">{asset.name}</h1>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs for Overview / Valuation */}
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="bg-[#0A0A0A] border border-[#262626] p-1">
                <TabsTrigger 
                  value="overview" 
                  className="data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="tab-overview"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger 
                  value="valuation"
                  className="data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="tab-valuation"
                >
                  <LineChart size={14} className="mr-1" />
                  Market Valuation
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="mt-6 space-y-6">
                {/* Valuation Card */}
                <div className="luxury-card p-6">
                  <h2 className="font-serif text-xl text-white mb-6">Valuation</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Purchase Price</p>
                      <p className="text-xl text-white">{formatCurrency(asset.purchase_price)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Current Value</p>
                      <p className="text-xl text-gold">{formatCurrency(asset.current_value)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Gain/Loss</p>
                      <p className={`text-xl flex items-center gap-1 ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                        {isPositive ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                        {formatCurrency(Math.abs(gain))}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Return</p>
                      <p className={`text-xl ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                        {isPositive ? '+' : ''}{gainPercent}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Details Card */}
                <div className="luxury-card p-6">
                  <h2 className="font-serif text-xl text-white mb-6">Details</h2>
                  <div className="space-y-4">
                    {asset.description && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Description</p>
                        <p className="text-white">{asset.description}</p>
                      </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Calendar size={18} className="text-gold mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Purchase Date</p>
                      <p className="text-white">{formatDate(asset.purchase_date)}</p>
                    </div>
                  </div>
                  {asset.location && (
                    <div className="flex items-start gap-3">
                      <MapPin size={18} className="text-gold mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Location</p>
                        <p className="text-white">{asset.location}</p>
                      </div>
                    </div>
                  )}
                </div>
                {asset.notes && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Notes</p>
                    <p className="text-muted-foreground">{asset.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Insurance Card */}
            {(asset.insurance_provider || asset.insurance_policy) && (
              <div className="luxury-card p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Shield size={20} className="text-gold" />
                  <h2 className="font-serif text-xl text-white">Insurance</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {asset.insurance_provider && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Provider</p>
                      <p className="text-white">{asset.insurance_provider}</p>
                    </div>
                  )}
                  {asset.insurance_policy && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Policy Number</p>
                      <p className="text-white font-mono">{asset.insurance_policy}</p>
                    </div>
                  )}
                  {asset.insurance_expiry && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Expiry Date</p>
                      <p className="text-white">{formatDate(asset.insurance_expiry)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Maintenance Records */}
            <div className="luxury-card p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Wrench size={20} className="text-gold" />
                  <h2 className="font-serif text-xl text-white">Maintenance</h2>
                </div>
                <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-gold hover:text-gold/80" data-testid="add-maintenance-btn">
                      <Plus size={16} className="mr-1" /> Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0F0F0F] border-[#262626]">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-white">Schedule Maintenance</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddMaintenance} className="space-y-4">
                      <div>
                        <Label className="text-muted-foreground">Title</Label>
                        <Input 
                          value={maintenanceForm.title}
                          onChange={(e) => setMaintenanceForm({...maintenanceForm, title: e.target.value})}
                          className="luxury-input"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Description</Label>
                        <Input 
                          value={maintenanceForm.description}
                          onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})}
                          className="luxury-input"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Scheduled Date</Label>
                        <Input 
                          type="date"
                          value={maintenanceForm.scheduled_date}
                          onChange={(e) => setMaintenanceForm({...maintenanceForm, scheduled_date: e.target.value})}
                          className="luxury-input"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Estimated Cost</Label>
                        <Input 
                          type="number"
                          value={maintenanceForm.cost}
                          onChange={(e) => setMaintenanceForm({...maintenanceForm, cost: e.target.value})}
                          className="luxury-input"
                          placeholder="0.00"
                        />
                      </div>
                      <Button type="submit" className="btn-primary w-full">Schedule</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {maintenance.length > 0 ? (
                <div className="space-y-3">
                  {maintenance.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A]"
                      data-testid={`maintenance-item-${item.id}`}
                    >
                      <div>
                        <p className="text-white font-medium">{item.title}</p>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gold">{formatDate(item.scheduled_date)}</p>
                          {item.cost && <p className="text-xs text-muted-foreground">{formatCurrency(item.cost)}</p>}
                        </div>
                        <button 
                          onClick={() => handleDeleteMaintenance(item.id)}
                          className="text-muted-foreground hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No maintenance records</p>
              )}
            </div>

            {/* Documents */}
            <div className="luxury-card p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <FileText size={20} className="text-gold" />
                  <h2 className="font-serif text-xl text-white">Documents</h2>
                </div>
                <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-gold hover:text-gold/80" data-testid="add-document-btn">
                      <Plus size={16} className="mr-1" /> Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0F0F0F] border-[#262626]">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-white">Add Document</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddDocument} className="space-y-4">
                      <div>
                        <Label className="text-muted-foreground">Name</Label>
                        <Input 
                          value={documentForm.name}
                          onChange={(e) => { const v = e.target.value; setDocumentForm(prev => ({...prev, name: v})); }}
                          className="luxury-input"
                          required
                          data-testid="document-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Type</Label>
                        <Select 
                          value={documentForm.document_type} 
                          onValueChange={(value) => setDocumentForm(prev => ({...prev, document_type: value}))}
                        >
                          <SelectTrigger className="luxury-input" data-testid="document-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0F0F0F] border-[#262626]">
                            <SelectItem value="insurance">Insurance</SelectItem>
                            <SelectItem value="certificate">Certificate</SelectItem>
                            <SelectItem value="receipt">Receipt</SelectItem>
                            <SelectItem value="warranty">Warranty</SelectItem>
                            <SelectItem value="valuation">Valuation</SelectItem>
                            <SelectItem value="provenance">Provenance</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Attach a file <span className="text-[10px] text-muted-foreground/70">(PDF, JPG, PNG · max 10MB)</span></Label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,application/pdf,image/*,text/plain,text/csv"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) { setDocumentForm(prev => ({...prev, file: null})); return; }
                            if (f.size > 10 * 1024 * 1024) {
                              toast.error('File must be under 10 MB');
                              e.target.value = '';
                              return;
                            }
                            setDocumentForm(prev => ({
                              ...prev,
                              file: f,
                              name: prev.name || f.name.replace(/\.[^.]+$/, ''),
                            }));
                          }}
                          className="block w-full text-xs text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-gold/15 file:text-gold file:text-xs file:cursor-pointer hover:file:bg-gold/25 cursor-pointer"
                          data-testid="document-file-input"
                        />
                        {documentForm.file && (
                          <p className="text-[10px] text-emerald-400 mt-1" data-testid="document-file-selected">
                            ✓ {documentForm.file.name} ({(documentForm.file.size / 1024 / 1024).toFixed(2)} MB)
                          </p>
                        )}
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Or paste a URL <span className="text-[10px] text-muted-foreground/70">(if hosted elsewhere)</span></Label>
                        <Input 
                          value={documentForm.file_url}
                          onChange={(e) => { const v = e.target.value; setDocumentForm(prev => ({...prev, file_url: v})); }}
                          className="luxury-input"
                          placeholder="https://..."
                          disabled={!!documentForm.file}
                          data-testid="document-url-input"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Notes</Label>
                        <Input 
                          value={documentForm.notes}
                          onChange={(e) => { const v = e.target.value; setDocumentForm(prev => ({...prev, notes: v})); }}
                          className="luxury-input"
                          data-testid="document-notes-input"
                        />
                      </div>
                      <Button type="submit" className="btn-primary w-full" disabled={uploadingDoc} data-testid="document-submit-btn">
                        {uploadingDoc ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading...</> : (documentForm.file ? 'Upload Document' : 'Add Document')}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed" data-testid="documents-section-help">
                Attach evidence of insurance, ownership and value for this asset. Recommended: insurance schedule, original receipt, certificate of authenticity, and a recent valuation. Click "Add" for a full guide.
              </p>

              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div 
                      key={doc.id} 
                      className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A]"
                      data-testid={`document-item-${doc.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={18} className="text-gold" />
                        <div>
                          <p className="text-white font-medium">{doc.name}</p>
                          <p className="text-xs text-muted-foreground uppercase">{doc.document_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {doc.has_file && (
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const url = api.getDocumentDownloadUrl(doc.id);
                                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                                if (!res.ok) throw new Error('failed');
                                const blob = await res.blob();
                                const blobUrl = URL.createObjectURL(blob);
                                window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                              } catch { toast.error('Could not open file'); }
                            }}
                            className="text-gold text-sm hover:underline"
                            data-testid={`document-open-${doc.id}`}
                          >
                            Open
                          </button>
                        )}
                        {!doc.has_file && doc.file_url && (
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gold text-sm hover:underline"
                          >
                            View
                          </a>
                        )}
                        <button 
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="text-muted-foreground hover:text-red-400 transition-colors"
                          data-testid={`document-delete-${doc.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No documents</p>
              )}
            </div>
              </TabsContent>
              
              {/* Market Valuation Tab */}
              <TabsContent value="valuation" className="mt-6 space-y-6">
                {/* Valuation History - shows historical AI valuations */}
                <ValuationHistory 
                  assetId={id} 
                  assetName={asset.name}
                  onValueAccepted={(newValue) => {
                    setAsset({...asset, current_value: newValue});
                  }}
                />
                
                {/* New Valuation Request */}
                <AssetValuation 
                  assetId={id} 
                  assetName={asset.name} 
                  currentValue={asset.current_value}
                  category={asset.category}
                  purchasePrice={asset.purchase_price}
                  onValueUpdate={(newValue) => {
                    setAsset({...asset, current_value: newValue});
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar Actions */}
          <div className="space-y-4">
            {/* Tokenisation, Digital Twin & Provenance UI hidden behind feature flag */}
            {FEATURES.TOKENISATION_ENABLED && asset.token_status && asset.token_status !== 'failed' ? (
              <div className="space-y-4">
                <DigitalTwinViewer
                  asset={asset}
                  token={tokenData}
                  ownershipPercentage={tokenData ? ((tokenData.fractions_available / tokenData.total_fractions) * 100) : 100}
                />
                {user?.digital_twin_access === 'active' && (asset.token_status === 'minted' || asset.token_status === 'pending') && (
                  <button
                    onClick={async () => {
                      try {
                        const tokens = await api.getMyTokens();
                        const token = tokens.data?.find(t => t.asset_id === asset.id);
                        if (!token) { toast.error('Token not found'); return; }
                        // First check readiness
                        const ready = await api.getSellerReadiness();
                        if (!ready.data?.ready) {
                          setPendingTokenIdToList(token.id);
                          setShowSellerReadiness(true);
                          return;
                        }
                        await api.listTokenForSale(token.id);
                        toast.success('Listed on marketplace.');
                        fetchData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to list');
                      }
                    }}
                    className="block w-full text-center text-xs text-[#627EEA] hover:text-[#8BA0F0] transition-colors"
                    data-testid="list-on-marketplace-btn"
                  >
                    List on Marketplace
                  </button>
                )}
              </div>
            ) : FEATURES.TOKENISATION_ENABLED && user?.digital_twin_access === 'active' ? (
              <Button
                onClick={() => setShowTokenizeModal(true)}
                className="w-full bg-gradient-to-r from-gold/80 to-gold text-black font-semibold hover:from-gold hover:to-gold/90 flex items-center justify-center gap-2 py-5"
                data-testid="tokenize-asset-btn"
              >
                <KeyRound size={16} />
                Activate Digital Twin
              </Button>
            ) : FEATURES.TOKENISATION_ENABLED ? (
              <DigitalTwinAccessCard user={user} onStatusChange={refreshUser} />
            ) : null}

            {/* Shareable Provenance Record - available once tokenised, tailored for advisors/insurers */}
            {FEATURES.TOKENISATION_ENABLED && asset.token_status === 'minted' && tokenData && (
              <ProvenanceShareCard
                assetId={id}
                tokenId={tokenData.id}
                initialShowValuation={!!tokenData.provenance_show_valuation}
              />
            )}

            <Link to={`/assets/${id}/edit`}>
              <Button className="btn-secondary w-full flex items-center justify-center gap-2" data-testid="edit-asset-btn">
                <Edit size={16} />
                Edit Asset
              </Button>
            </Link>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full border-red-900/50 text-red-400 hover:bg-red-900/20 hover:text-red-300"
                  data-testid="delete-asset-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete Asset
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0F0F0F] border-[#262626]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Delete this asset?</AlertDialogTitle>
                  <AlertDialogDescription className="text-muted-foreground">
                    This will permanently delete "{asset.name}" and all associated maintenance records and documents.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-[#1A1A1A] border-[#262626] text-white hover:bg-[#262626]">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-red-900 text-white hover:bg-red-800">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Tokenize Ceremony - hidden behind feature flag */}
        {FEATURES.TOKENISATION_ENABLED && (
          <TokenizeCeremony
            open={showTokenizeModal}
            onOpenChange={setShowTokenizeModal}
            asset={asset}
            onSuccess={() => fetchData()}
          />
        )}

        {/* Seller Onboarding Checklist - only relevant when tokenisation enabled */}
        {FEATURES.TOKENISATION_ENABLED && (
          <SellerReadiness
            open={showSellerReadiness}
            onClose={() => { setShowSellerReadiness(false); setPendingTokenIdToList(null); }}
            onReady={async () => {
              if (!pendingTokenIdToList) return;
              try {
                await api.listTokenForSale(pendingTokenIdToList);
                toast.success('Listed on marketplace.');
                setShowSellerReadiness(false);
                setPendingTokenIdToList(null);
                fetchData();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to list');
              }
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}


// Compact component for Digital Twin access request/status
function DigitalTwinAccessCard({ user, onStatusChange }) {
  const [loading, setLoading] = useState(false);
  const status = user?.digital_twin_access || 'none';
  const isElite = user?.subscription_tier === 'elite';

  const handleRequest = async () => {
    setLoading(true);
    try {
      await api.requestDigitalTwinAccess();
      toast.success('Request submitted. Our team will review it shortly.');
      if (onStatusChange) onStatusChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const res = await api.createDigitalTwinCheckout();
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      // If Stripe fails, try direct activation for demo
      try {
        await api.activateDigitalTwinAccess();
        toast.success('Digital Twin Vault activated!');
        if (onStatusChange) onStatusChange();
      } catch (e2) {
        toast.error(e2.response?.data?.detail || 'Payment failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="luxury-card p-5 space-y-4" data-testid="dt-access-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 border border-gold/30 flex items-center justify-center">
          <KeyRound size={18} className="text-gold" />
        </div>
        <div>
          <h3 className="text-white font-serif text-base">Digital Twin Vault</h3>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Exclusive Access</p>
        </div>
      </div>

      {status === 'none' && !isElite && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            The Digital Twin Vault is available exclusively to Elite members. Upgrade your membership to request access.
          </p>
          <Link to="/pricing">
            <Button className="w-full btn-secondary text-xs" data-testid="upgrade-for-dt-btn">
              Upgrade to Elite
            </Button>
          </Link>
        </div>
      )}

      {status === 'none' && isElite && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Transform your physical assets into blockchain-secured Digital Twins. Request access to unlock tokenization, 3D model generation, and the fractional marketplace.
          </p>
          <Button
            onClick={handleRequest}
            disabled={loading}
            className="w-full bg-gradient-to-r from-gold/80 to-gold text-black font-semibold hover:from-gold hover:to-gold/90 text-xs py-3"
            data-testid="request-dt-access-btn"
          >
            {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <KeyRound size={14} className="mr-2" />}
            Request Access
          </Button>
          <p className="text-[10px] text-center text-muted-foreground">One-time fee of £499 after approval</p>
        </div>
      )}

      {status === 'requested' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 bg-gold/5 border border-gold/20 rounded">
            <div className="w-2 h-2 rounded-full bg-gold animate-pulse" />
            <p className="text-xs text-gold">Under Review</p>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Your request is being reviewed by our team. We'll notify you once a decision has been made.
          </p>
        </div>
      )}

      {status === 'approved' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded">
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
            <p className="text-xs text-emerald-400">Approved. Awaiting Payment</p>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Your access has been approved. Complete payment to activate the Digital Twin Vault.
          </p>
          <Button
            onClick={handlePayment}
            disabled={loading}
            className="w-full bg-gradient-to-r from-gold/80 to-gold text-black font-semibold hover:from-gold hover:to-gold/90 text-xs py-3"
            data-testid="pay-dt-access-btn"
          >
            {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
            Activate for £499
          </Button>
        </div>
      )}
    </div>
  );
}
